/**
 * useSubscriptionTransactions — SOLE network owner for
 * GET subscriptions/{id}/transactions.
 *
 * Module-scope singleton: every caller shares the same reactive `rows` ref.
 * `fetchTransactions(subscription_id)` is the single network call.
 *
 * Display note: amount_cents is the raw integer from the contract.
 * Each row also carries `currency` (from the contract, defaults to 'BRL') and
 * `amount_major = amount_cents / currencyFactor(currency)` for display via n()
 * with the row currency. Callers must use n(row.amount_major, { style: 'currency',
 * currency: row.currency }) — never a bare /100 where currency can vary.
 *
 * All network lives here (no_httpservice_in_vue rule).
 * Call `_reset()` in afterEach for test isolation.
 */
import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import { currencyFactor } from '../utils/currencyMinorUnits'
import type { Subscriptions_TransactionRow } from '../contracts/Subscriptions_TransactionRow'
import type { Subscriptions_TransactionsResponseDados } from '../contracts/Subscriptions_TransactionsResponse'

// Side-effect import: registers the contract with the responseContract registry.
import '../contracts/Subscriptions_TransactionsResponse'
import '../contracts/Subscriptions_TransactionRow'

// ── Display-augmented row type ────────────────────────────────────────────────

export interface Subscriptions_TransactionRowDisplay extends Subscriptions_TransactionRow {
  /** Row currency (from contract, defaults to 'BRL'). */
  currency: string
  /**
   * amount_cents converted to major units via currencyFactor for that currency.
   * BRL/USD: amount_cents / 100. CLP: amount_cents / 1 (zero-decimal).
   * Use n(amount_major, { style: 'currency', currency }) for display.
   */
  amount_major: number
}

// ── Module-scope singleton state ────────────────────────────────────────────

const rows = ref<Subscriptions_TransactionRowDisplay[]>([])

// ── Public composable ────────────────────────────────────────────────────────

export function useSubscriptionTransactions() {
  /**
   * fetchTransactions — GET subscriptions/{subscription_id}/transactions.
   *
   * Unwraps resp.dados.rows ?? [] into the module-scope `rows` ref.
   * Each row is augmented with `currency` (defaults to 'BRL') and
   * `amount_major = amount_cents / currencyFactor(currency)`.
   * On error: logs and re-throws (no silent catch — the caller sees the error).
   */
  async function fetchTransactions(subscription_id: number): Promise<void> {
    try {
      const raw = await httpService.get(
        `${config.api.sistemaHost}/subscriptions/${subscription_id}/transactions`,
        { responseContract: 'Subscriptions_TransactionsResponse' },
      )
      const resp = raw as { dados: Subscriptions_TransactionsResponseDados }
      const rawRows = resp.dados?.rows ?? []
      rows.value = rawRows.map((row) => {
        const currency = row.currency ?? 'BRL'
        return {
          ...row,
          currency,
          amount_major: (row.amount_cents ?? 0) / currencyFactor('pt-BR', currency),
        }
      })
      log('useSubscriptionTransactions', 'fetchTransactions', 'Transações carregadas', {
        subscription_id,
        count: rows.value.length,
      })
    } catch (err) {
      log('useSubscriptionTransactions', 'fetchTransactions', 'Erro ao carregar transações', {
        subscription_id,
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    }
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    rows.value = []
  }

  return {
    rows,
    fetchTransactions,
    _reset,
  }
}
